import { Card, CircularProgress } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

import { FaArrowLeft, FaTrashRestore } from "react-icons/fa";

import useCommon from "hooks/useCommon";
import colors from "assets/theme/base/colors";

import { useEffect, useState } from "react";
import { getDeletedOperationsTemplates } from "store/slice/operationsSlice";
import { restoreOperationsTemplate } from "store/slice/operationsSlice";
import Snackbar from "components/Snackbar";
import dayjs from "dayjs";

const deletedTemplatesLoading = false;
const restoreLoading = false;

const deletedTemplates = [
  {
    _id: "template_1",
    name: "Morning Operations Checklist",
    createdAt: "2026-05-01T10:30:00Z",
    deletedAt: "2026-06-12T10:30:00Z",
    deletedBy: "Haziq Sheikh",
    sectionsCount: 8,
  },
  {
    _id: "template_2",
    name: "Weekend Operations Template",
    createdAt: "2026-05-05T14:00:00Z",
    deletedAt: "2026-06-10T14:15:00Z",
    deletedBy: "Max",
    sectionsCount: 12,
  },
  {
    _id: "template_3",
    name: "Housekeeping Pass Down",
    createdAt: "2026-05-08T09:00:00Z",
    deletedAt: "2026-06-08T08:00:00Z",
    deletedBy: "Andrew",
    sectionsCount: 6,
  },
];

const DeletedOperationsTemplates = () => {
  const { DISPATCH, STORE, NAVIGATE } = useCommon();
  const { light } = colors;

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const {
    deletedTemplates,
    deletedTemplatesLoading,
    deletedTemplatesError,
    deletedTemplatesPagination,

    restoreLoading,
    restoreSuccess,
    restoreError,
  } = STORE((state) => state.operations);

  const [restoringTemplateId, setRestoringTemplateId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const showSnackbar = (data) =>
    setSnackbar({
      show: true,
      ...data,
    });

  const closeSnackbar = () =>
    setSnackbar((prev) => ({
      ...prev,
      show: false,
    }));

  const handleRestore = async (template) => {
    try {
      setRestoringTemplateId(template.templateId);

      const res = await DISPATCH(restoreOperationsTemplate(template.templateId)).unwrap();

      if (res?.success) {
        showSnackbar({
          color: "success",
          title: "Template Restored",
          content: res?.message || "Operations template restored successfully",
          icon: "check",
        });

        DISPATCH(
          getDeletedOperationsTemplates({
            hotelId: hotelIds[0],
            page: 1,
            limit: 50,
            search: "",
          })
        );
      }
    } catch (err) {
      showSnackbar({
        color: "error",
        title: "Restore Failed",
        content: err?.message || err || "Failed to restore template",
        icon: "warning",
      });
    } finally {
      setRestoringTemplateId(null);
    }
  };

  useEffect(() => {
    if (!hotelIds?.[0]) return;

    DISPATCH(
      getDeletedOperationsTemplates({
        hotelId: hotelIds[0],
        page: 1,
        limit: 50,
        search: "",
      })
    );
  }, [DISPATCH, hotelIds]);

  const tableData = {
    columns: [
      { name: "template", align: "left" },
      { name: "created_at", align: "center" },
      { name: "deleted_at", align: "center" },
      { name: "deleted_by", align: "center" },
      { name: "actions", align: "center" },
    ],

    rows: deletedTemplates.map((template) => ({
      template: (
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" fontWeight="medium">
            {template.templateName}
          </VuiTypography>

          <VuiTypography variant="caption">{template.sectionCount} Sections</VuiTypography>
        </VuiBox>
      ),

      created_at: (
        <VuiTypography variant="caption">
          {template.createdAt ? dayjs(template.createdAt.split("T")[0]).format("MM/DD/YYYY") : "-"}
        </VuiTypography>
      ),

      deleted_at: (
        <VuiTypography variant="caption">
          {template.deletedAt ? dayjs(template.deletedAt.split("T")[0]).format("MM/DD/YYYY") : "-"}
        </VuiTypography>
      ),

      deleted_by: (
        <VuiTypography variant="caption">{template.deletedBy?.name || "-"}</VuiTypography>
      ),

      actions: (
        <VuiButton
          color="primary"
          size="small"
          disabled={restoringTemplateId === template.templateId || !template.canRestore}
          onClick={() => handleRestore(template)}
        >
          {restoringTemplateId === template.templateId ? (
            <VuiBox display="flex" alignItems="center" gap={1}>
              <CircularProgress size={12} />
              Restoring...
            </VuiBox>
          ) : (
            <>
              <FaTrashRestore
                size={10}
                style={{
                  marginRight: 6,
                }}
              />
              Restore
            </>
          )}
        </VuiButton>
      ),
    })),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Card>
        <VuiBox p={1}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <VuiBox>
              <VuiTypography variant="h4">Deleted Templates</VuiTypography>

              <VuiTypography variant="caption">
                Restore previously deleted operations templates
              </VuiTypography>
            </VuiBox>

            <VuiButton
              variant="outlined"
              color="primary"
              onClick={() => NAVIGATE("/daily-operations")}
            >
              <FaArrowLeft size={11} style={{ marginRight: 6 }} />
              Back
            </VuiButton>
          </VuiBox>

          {deletedTemplatesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deletedTemplatesError?.message || deletedTemplatesError}
            </Alert>
          )}

          {!deletedTemplatesLoading && deletedTemplates?.length === 0 ? (
            <VuiBox py={10} display="flex" flexDirection="column" alignItems="center">
              <FaTrashRestore size={40} color="#718096" />

              <VuiTypography mt={2} fontWeight="bold">
                No Deleted Templates
              </VuiTypography>

              <VuiTypography variant="caption">
                There are currently no deleted operations templates.
              </VuiTypography>
            </VuiBox>
          ) : deletedTemplatesLoading ? (
            <VuiBox py={10} display="flex" justifyContent="center">
              <CircularProgress />
            </VuiBox>
          ) : (
            <Table columns={tableData.columns} rows={tableData.rows} />
          )}
        </VuiBox>
      </Card>

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
};

export default DeletedOperationsTemplates;
